import { WeeklyComparisonChart } from '../../components/WeeklyComparisonChart';
import { displayDigits } from '../../utils/displayDigits';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmCard } from '../../components/NmCard';
import { NmSectionHeader } from '../../components/NmSectionHeader';
import { NmTabNav } from '../../components/NmTabNav';
import type { WeeklySummaryData } from '../../services/api';
import { secToHm } from '../../utils/homeHelpers';
import '../../styles/weekly-summary.css';

type ModeId = 1 | 2 | 3;

interface Props {
  data: WeeklySummaryData;
}

function PlanToActualBridge({ progress, hasPlan }: { progress: number; hasPlan: boolean }) {
  const { t } = useTranslation();
  const percent = Math.max(0, Math.round(progress * 100));
  return (
    <div className="weekly-progress">
      {!hasPlan && <span>{t('weeklyProgress.noPlan')}</span>}
      {hasPlan && <div className="weekly-progress__track" role="progressbar" aria-label={t('weeklyProgress.completed')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(100, percent)} aria-valuetext={`${displayDigits(percent)}%`}>
        <span style={{ width: `${Math.min(100, percent)}%` }} />
      </div>}
    </div>
  );
}

export function WeeklySummaryCard({ data }: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ModeId>(1);
  const summary = data['weekly-summary'];

  const modes = useMemo(
    () => [
      { value: 1 as ModeId, label: t('home.weekly.time', 'Time') },
      { value: 2 as ModeId, label: t('home.weekly.distance', 'Distance') },
      { value: 3 as ModeId, label: t('home.weekly.tss', 'TSS') },
    ],
    [t],
  );

  const actual = useMemo(() => {
    if (mode === 1) return secToHm(summary.totalTime ?? 0);
    if (mode === 2) return `${((summary.totalDistance ?? 0) / 1000).toFixed(1)} km`;
    return String(Math.round(summary.totalTSS ?? 0));
  }, [mode, summary]);

  const planned = useMemo(() => {
    if (mode === 1) return secToHm(summary.totalTimePlanned ?? 0);
    if (mode === 2) return `${((summary.totalDistancePlanned ?? 0) / 1000).toFixed(1)} km`;
    return String(Math.round(summary.totalTSSPlanned ?? 0));
  }, [mode, summary]);

  const plannedValue = mode === 1 ? summary.totalTimePlanned ?? 0 : mode === 2 ? summary.totalDistancePlanned ?? 0 : summary.totalTSSPlanned ?? 0;
  const hasPlan = plannedValue > 0;

  const progress = useMemo(() => {
    const plannedVal =
      mode === 1
        ? summary.totalTimePlanned ?? 0
        : mode === 2
          ? summary.totalDistancePlanned ?? 0
          : summary.totalTSSPlanned ?? 0;
    const actualVal =
      mode === 1
        ? summary.totalTime ?? 0
        : mode === 2
          ? summary.totalDistance ?? 0
          : summary.totalTSS ?? 0;
    if (plannedVal <= 0) return actualVal > 0 ? 1 : 0;
    return actualVal / plannedVal;
  }, [mode, summary]);


  return (
    <NmCard className="weekly-summary-card" variant="raised-sm" style={{ marginBottom: 12 }}>
      <NmSectionHeader title={t('home.weekly.title', 'This week')} />
      <NmTabNav
        className="weekly-summary__metric-tabs home-metric-tabs"
        items={modes}
        value={mode}
        onChange={setMode} ariaLabel={t('home.weekly.title', 'This week')}
      />

      <div className="weekly-plan-flow">
        <div className="weekly-plan-flow__side weekly-plan-flow__side--planned">
          <div className="weekly-plan-flow__label">{t('home.weekly.planned', 'Planned')}</div>
          <div className="weekly-plan-flow__value">{displayDigits(planned)}</div>
        </div>

        <PlanToActualBridge progress={progress} hasPlan={hasPlan} />

        <div className="weekly-plan-flow__side weekly-plan-flow__side--actual">
          <div className="weekly-plan-flow__label">{t('home.weekly.actual', 'Completed')}</div>
          <div className="weekly-plan-flow__value-row">
            <div className="weekly-plan-flow__value">{displayDigits(actual)}</div>
          </div>
        </div>
      </div>


      <WeeklyComparisonChart daily={data.daily} metric={mode} />

    </NmCard>
  );
}
